import React, { FC, memo, useRef, useState, useEffect, useCallback, useContext, useMemo } from 'react'
import {
    ArrowContainer,
    ArrowRightIcon,
    Container,
    ContentContainer,
    ContentSection,
    ContentWrapper,
    CustomMarkdown,
    HeadingImageWrapper,
    ImageWrapper,
    SectionHeading,
    SectionImage,
    SectionLink,
    SectionLinkContainer,
    SectionLinksList,
    SectionLinkTitle,
    SidebarContainer,
} from './styles'
import {
    RegionOverviewSectionTransport,
    RegionSummaryTransport,
} from '../../../transports/region-landing-page-transport'
import { useTranslation } from '../../../hooks/locale'
import { slugifyText } from '../utils'
import { ScreenContext } from 'react-components'
import { Subtitle, Title, TitleContainer } from '../common/styles'

interface RegionOverviewProps {
    regionOverviewSection: RegionOverviewSectionTransport
    location: string
    regionSummary?: RegionSummaryTransport | null
}

const getSectionSlug = (title: string | null | undefined, index: number) => {
    return title ? slugifyText(title) : `section-${index}`
}

const RegionOverviewSection: FC<RegionOverviewProps> = ({ regionOverviewSection, location, regionSummary }) => {
    const { t } = useTranslation()

    const hasRegionOverviewList = regionOverviewSection?.regionOverviewList?.length > 0
    const useRegionSummary = !hasRegionOverviewList && regionSummary?.items?.length

    const regionSummaryList = useMemo(
        () =>
            regionSummary?.items?.map(item => ({
                title: item.title,
                content: item.text,
                images: [],
                type: '',
                icon: '',
            })) ?? [],
        [regionSummary?.items],
    )

    const regionOverviewList = useMemo(
        () => (hasRegionOverviewList ? regionOverviewSection?.regionOverviewList : regionSummaryList),
        [hasRegionOverviewList, regionOverviewSection?.regionOverviewList, regionSummaryList],
    )

    const regionOverviewSectionTitle =
        regionOverviewSection?.heading || regionSummary?.heading || `${t('regionOverviewSectionTitle')} ${location}`

    const regionOverviewSectionSubtitle =
        (useRegionSummary ? regionSummary?.subheading : null) || `${t('regionOverviewSectionSubTitle')} ${location}`

    const { isMobile } = useContext(ScreenContext)

    const sectionsRef = useRef<(Element | null)[]>([])
    const headingsRef = useRef<(HTMLLIElement | null)[]>([])
    const [activeIndex, setActiveIndex] = useState<number>(0)
    const [isScrolling, setIsScrolling] = useState<boolean>(false)

    const SCROLL_OFFSET = 40

    const scrollHeadingIntoView = (index: number) => {
        const el = headingsRef.current[index]
        const container = el?.parentElement?.parentElement

        if (el && container) {
            container.scrollTo({
                top: el.offsetTop - SCROLL_OFFSET,
                behavior: 'smooth',
            })
        }
    }

    const handleClick = useCallback((index: number) => {
        setIsScrolling(true)
        sectionsRef.current[index]?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
        scrollHeadingIntoView(index)
        setActiveIndex(index)

        setTimeout(() => {
            setIsScrolling(false)
        }, 1000)
    }, [])

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '-20% 0px -70% 0px',
            threshold: 0,
        }

        const observerCallback = (entries: IntersectionObserverEntry[]) => {
            if (isScrolling) {
                return
            }

            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const index = sectionsRef.current.indexOf(entry.target)
                    if (index !== -1) {
                        setActiveIndex(index)
                        scrollHeadingIntoView(index)
                    }
                }
            })
        }

        const observer = new IntersectionObserver(observerCallback, observerOptions)

        sectionsRef.current.forEach(section => {
            if (section) {
                observer.observe(section)
            }
        })

        return () => {
            observer.disconnect()
        }
    }, [isScrolling, regionOverviewList.length])

    return (
        <Container id="region-overview">
            <TitleContainer>
                <Title>{regionOverviewSectionTitle}</Title>
                <Subtitle>{regionOverviewSectionSubtitle}</Subtitle>
            </TitleContainer>
            <ContentWrapper>
                <SidebarContainer>
                    <SectionLinksList>
                        {regionOverviewList.map((item, i) => {
                            if (!item.title) {
                                return null
                            }
                            const sectionSlug = getSectionSlug(item.title, i)
                            return (
                                <SectionLinkContainer
                                    key={item.title}
                                    active={activeIndex === i}
                                    ref={el => (headingsRef.current[i] = el)}
                                >
                                    <SectionLink
                                        href={`#${sectionSlug}`}
                                        onClick={e => {
                                            e.preventDefault()
                                            handleClick(i)
                                        }}
                                    >
                                        <SectionLinkTitle>{item.title}</SectionLinkTitle>
                                        <ArrowContainer active={activeIndex === i}>
                                            <ArrowRightIcon />
                                        </ArrowContainer>
                                    </SectionLink>
                                </SectionLinkContainer>
                            )
                        })}
                    </SectionLinksList>
                </SidebarContainer>
                <ContentContainer>
                    {regionOverviewList.map((item, i) => {
                        const sectionSlug = getSectionSlug(item.title, i)
                        const imageUrl =
                            (isMobile && item.images?.[0]?.formats?.small?.url) ||
                            item.images?.[0]?.formats?.large?.url ||
                            item.images?.[0]?.url

                        return (
                            <ContentSection
                                key={`${item.title}-answer`}
                                ref={el => (sectionsRef.current[i] = el)}
                                id={sectionSlug}
                            >
                                <HeadingImageWrapper>
                                    {imageUrl && (
                                        <ImageWrapper>
                                            <SectionImage src={imageUrl} alt={item.title} loading="lazy" />
                                        </ImageWrapper>
                                    )}
                                    <SectionHeading>{item.title}</SectionHeading>
                                </HeadingImageWrapper>
                                <CustomMarkdown>{item.content}</CustomMarkdown>
                            </ContentSection>
                        )
                    })}
                </ContentContainer>
            </ContentWrapper>
        </Container>
    )
}

export default memo(RegionOverviewSection)
